import React, { useState, useRef } from 'react';
import { useUser } from '../../shared/context/UserContext';
import { getBolDocumentUploadUrl, addBolDocument, deleteBolDocument } from '../../api/disposalApi';

const MAX_FILE_SIZE = 52_428_800; // 50MB

const DOC_TYPES = [
  { value: 'WEIGHT_TICKET', label: 'Weight Ticket' },
  { value: 'BOL_SCAN', label: 'BOL Scan' },
  { value: 'OTHER', label: 'Other' },
];

export default function BolDocumentsModal({ bolId, projectId, existingDocuments = [], onSave, onClose }) {
  const { callerEmail } = useUser();
  const [docType, setDocType] = useState('WEIGHT_TICKET');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [documents, setDocuments] = useState(existingDocuments);
  const fileRef = useRef(null);

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (fileRef.current) fileRef.current.value = '';

    setError(null);
    setSuccess(null);

    if (file.size > MAX_FILE_SIZE) {
      setError('File exceeds 50MB limit');
      return;
    }

    setUploading(true);
    try {
      // 1. Get SAS upload URL from backend
      const { upload_url, blob_name } = await getBolDocumentUploadUrl(
        bolId, file.name, file.size, callerEmail
      );

      // 2. PUT file directly to blob storage
      const putRes = await fetch(upload_url, {
        method: 'PUT',
        headers: {
          'x-ms-blob-type': 'BlockBlob',
          'Content-Type': file.type || 'application/octet-stream',
        },
        body: file,
      });
      if (!putRes.ok) throw new Error('File upload to storage failed');

      // 3. Save metadata to BOL document via manage_bol
      const docMeta = {
        blob_name,
        original_filename: file.name,
        document_type: docType,
        file_size_bytes: file.size,
      };
      const result = await addBolDocument(bolId, projectId, docMeta, callerEmail);
      const updated = result.bol_documents || [...documents, { ...docMeta, uploaded_by: callerEmail, uploaded_at: new Date().toISOString() }];
      setDocuments(updated);
      onSave(updated);
      setSuccess('Document uploaded');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete(blobName) {
    if (!window.confirm('Delete this document?')) return;
    setError(null);
    try {
      const result = await deleteBolDocument(bolId, projectId, blobName, callerEmail);
      const updated = result.bol_documents || documents.filter(d => d.blob_name !== blobName);
      setDocuments(updated);
      onSave(updated);
      setSuccess('Document deleted');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    }
  }

  function fmtDate(iso) {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return iso; }
  }

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'var(--color-overlay)', zIndex: 'var(--z-modal)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={onClose}>
      <div style={{
        background: 'var(--color-bg-card)', borderRadius: 'var(--radius-xl)',
        maxWidth: '600px', width: '90vw', maxHeight: '80vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        boxShadow: 'var(--shadow-xl)',
      }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: 'var(--space-7) var(--space-8)',
          borderBottom: '1px solid var(--color-border)',
        }}>
          <h2 style={{ margin: 0, fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-bold)', color: 'var(--color-text-heading)' }}>
            BOL Documents
          </h2>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', fontSize: 'var(--text-xl)',
            color: 'var(--color-text-muted)', cursor: 'pointer', padding: 'var(--space-2)',
            fontFamily: 'var(--font-family)',
          }}>×</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--space-6) var(--space-8)' }}>
          {/* Upload controls */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', marginBottom: 'var(--space-6)' }}>
            <select
              value={docType}
              onChange={e => setDocType(e.target.value)}
              style={{
                padding: 'var(--space-3) var(--space-5)', fontSize: 'var(--text-sm)',
                border: '1px solid var(--color-border-input)', borderRadius: 'var(--radius-md)',
                fontFamily: 'var(--font-family)', background: 'var(--color-bg-card)', color: 'var(--color-text)',
              }}
            >
              {DOC_TYPES.map(dt => <option key={dt.value} value={dt.value}>{dt.label}</option>)}
            </select>
            <button
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
              style={{
                padding: 'var(--space-3) var(--space-6)', border: '1px solid var(--color-accent)',
                borderRadius: 'var(--radius-md)', background: 'transparent',
                color: 'var(--color-accent)', fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-semibold)', fontFamily: 'var(--font-family)',
                cursor: uploading ? 'not-allowed' : 'pointer', opacity: uploading ? 0.5 : 1,
              }}
            >
              {uploading ? 'Uploading…' : 'Upload File'}
            </button>
            <input ref={fileRef} type="file" onChange={handleUpload} style={{ display: 'none' }} />
          </div>

          {error && (
            <div style={{
              padding: 'var(--space-4) var(--space-6)', marginBottom: 'var(--space-4)',
              borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
              background: 'var(--color-danger-light)', color: 'var(--color-danger-dark)',
              border: '1px solid var(--color-danger)',
            }}>{error}</div>
          )}
          {success && (
            <div style={{
              padding: 'var(--space-4) var(--space-6)', marginBottom: 'var(--space-4)',
              borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
              background: 'var(--color-success-light)', color: 'var(--color-success-dark)',
              border: '1px solid var(--color-success)',
            }}>{success}</div>
          )}

          {/* Document list */}
          {documents.length === 0 ? (
            <p style={{ color: 'var(--color-text-placeholder)', fontSize: 'var(--text-sm)', fontStyle: 'italic' }}>
              No documents uploaded.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              {documents.map((doc, i) => (
                <div key={doc.blob_name || i} style={{
                  display: 'flex', alignItems: 'center', gap: 'var(--space-4)',
                  padding: 'var(--space-4) var(--space-5)',
                  background: 'var(--color-bg)', borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--color-border-light)',
                  fontSize: 'var(--text-sm)',
                }}>
                  <span style={{ fontWeight: 'var(--font-medium)', color: 'var(--color-text)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {doc.original_filename || doc.blob_name}
                  </span>
                  <span style={{
                    padding: 'var(--space-1) var(--space-3)', borderRadius: 'var(--radius-sm)',
                    fontSize: 'var(--text-2xs)', fontWeight: 'var(--font-semibold)',
                    background: 'var(--color-bg-active)', color: 'var(--color-text-muted)',
                  }}>
                    {doc.document_type || 'OTHER'}
                  </span>
                  <span style={{ fontSize: 'var(--text-2xs)', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
                    {doc.uploaded_by && `${doc.uploaded_by}`}
                    {doc.uploaded_at && ` · ${fmtDate(doc.uploaded_at)}`}
                  </span>
                  <button
                    onClick={() => handleDelete(doc.blob_name)}
                    style={{
                      background: 'none', border: 'none', color: 'var(--color-danger)',
                      fontSize: 'var(--text-xs)', cursor: 'pointer', fontFamily: 'var(--font-family)',
                      padding: 'var(--space-1) var(--space-3)', borderRadius: 'var(--radius-sm)',
                    }}
                  >Delete</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', justifyContent: 'flex-end',
          padding: 'var(--space-6) var(--space-8)',
          borderTop: '1px solid var(--color-border)',
        }}>
          <button onClick={onClose} style={{
            padding: 'var(--space-4) var(--space-8)', border: '1px solid var(--color-border-input)',
            borderRadius: 'var(--radius-md)', background: 'transparent',
            color: 'var(--color-text-secondary)', fontSize: 'var(--text-sm)',
            fontWeight: 'var(--font-semibold)', fontFamily: 'var(--font-family)', cursor: 'pointer',
          }}>Close</button>
        </div>
      </div>
    </div>
  );
}
